import {type ReactNode, useCallback, useMemo, useState} from 'react'
import {ThemeContext} from '@/hooks/useTheme'
import {deriveCssVars, isValidHex} from '@/lib/color-utils'

const STORAGE_KEY = 'redstack:primaryColor'
const DEFAULT_COLOR = '#1a73e8'

function applyCssVars(hex: string) {
	const vars = deriveCssVars(hex)
	if (!vars) return
	const style = document.documentElement.style
	for (const [key, value] of Object.entries(vars)) {
		style.setProperty(key, value)
	}
}

// Color picker temporarily disabled — accent is locked to the default.
// To re-enable, restore the localStorage read (see git history) + the Appearance section.
function getInitialColor(): string {
	return DEFAULT_COLOR
}

interface ThemeProviderProps {
	children: ReactNode
}

export function ThemeProvider({children}: ThemeProviderProps) {
	const [primaryColor, setPrimaryColorState] = useState(getInitialColor)

	const setPrimaryColor = useCallback((hex: string) => {
		if (!isValidHex(hex)) return
		applyCssVars(hex)
		setPrimaryColorState(hex)
		try {
			localStorage.setItem(STORAGE_KEY, hex)
		} catch {
			// localStorage unavailable
		}
	}, [])

	const value = useMemo(
		() => ({primaryColor, setPrimaryColor}),
		[primaryColor, setPrimaryColor],
	)

	return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
